import React, { useState, useEffect } from "react";
import {
  Modal,
  Text,
  TextInput,
  Textarea,
  Button,
  Stack,
  Group,
} from "@mantine/core";
import { IconSend, IconShare } from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";
import { inviteClientToPortal } from "../api";

interface InviteClientModalProps {
  opened: boolean;
  onClose: () => void;
  clientEmail: string;
  clientFirstName?: string;
  clientLastName?: string;
  onInviteSent?: () => void;
}

const InviteClientModal: React.FC<InviteClientModalProps> = ({
  opened,
  onClose,
  clientEmail,
  clientFirstName,
  clientLastName,
  onInviteSent,
}) => {
  const [message, setMessage] = useState(
    `Hi there,\n\nI've created your client account where you can securely access your tax documents and information. Please click the link below to set up your account.`
  );

  useEffect(() => {
    const name = [clientFirstName, clientLastName].filter(Boolean).join(" ");
    setMessage(
      `Hi ${name || "there"},\n\nI've created your client account where you can securely access your tax documents and information. Please click the link below to set up your account.`
    );
  }, [clientFirstName, clientLastName]);
  const [errors, setErrors] = useState<{ email?: string; message?: string }>(
    {}
  );
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const validate = () => {
    const newErrors: { email?: string; message?: string } = {};
    if (!clientEmail) {
      newErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientEmail)) {
      newErrors.email = "Please enter a valid email address.";
    }
    if (!message || message.trim() === "") {
      newErrors.message = "Message is required.";
    }
    return newErrors;
  };

  const handleSend = async () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmSend = async () => {
    setLoading(true);
    try {
      const res = await inviteClientToPortal(clientEmail, message);
      showNotification({
        title: "Invitation Sent",
        message: res.message || `Invitation email sent to ${clientEmail}`,
        color: "green",
        icon: <IconSend size={18} />,
      });
      onClose();
      setErrors({});
      if (onInviteSent) onInviteSent();
    } catch (err: any) {
      showNotification({
        title: "Failed to send invite",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Something went wrong.",
        color: "red",
      });
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={
          <Group align="center" gap={8}>
            <IconShare />
            <Text fw={600} size="lg">
              Invite Your Client to Portal
            </Text>
          </Group>
        }
        centered
        padding="lg"
        radius="md"
        size="lg"
        overlayProps={{ opacity: 0.55, blur: 3 }}
        styles={{
          title: { paddingBottom: 0 },
          header: { borderBottom: "none" },
          body: { paddingTop: 0 },
        }}
      >
        <Text c="dimmed" mb="md">
          Invite your client to their secure portal where they can check
          documents and easily reach out to you.
        </Text>
        <Stack gap="sm">
          <div>
            <Text fw={500} mb={4}>
              Enter email to invite
            </Text>
            <TextInput
              placeholder="Client email"
              value={clientEmail}
              disabled
              mb="md"
              size="md"
              radius="md"
              error={errors.email}
            />
            {errors.email && (
              <Text c="red" size="xs" mt={-12} mb={8}>
                {errors.email}
              </Text>
            )}
          </div>
          <div>
            <Text fw={500} mb={2}>
              Add your custom message
            </Text>
            <Text c="dimmed" size="sm" mb={4}>
              This message will be added to your email
            </Text>
            <Textarea
              minRows={6}
              autosize
              maxRows={12}
              value={message}
              onChange={(e) => setMessage(e.currentTarget.value)}
              size="md"
              radius="md"
              style={{ minHeight: 120 }}
              error={errors.message}
            />
            {errors.message && (
              <Text c="red" size="xs" mt={-12} mb={8}>
                {errors.message}
              </Text>
            )}
          </div>
          <Button
            fullWidth
            mt="md"
            size="md"
            color="blue"
            variant="filled"
            leftSection={<IconSend size={18} />}
            onClick={handleSend}
            style={{ marginTop: 24 }}
            loading={loading}
          >
            Send email
          </Button>
        </Stack>
      </Modal>
      <Modal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={<Text fw={600}>Confirm Email Invitation</Text>}
        centered
        padding="lg"
        radius="md"
        size="md"
        overlayProps={{ opacity: 0.55, blur: 3 }}
      >
        <Text mb="md">
          Are you sure you want to send a portal invitation to{" "}
          <b>{clientEmail}</b>?
          <br />
          The client will receive an email with instructions to set up their
          account and securely access their portal. They will be able to log in
          and start using the portal immediately.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button color="blue" onClick={handleConfirmSend} loading={loading}>
            Yes, Send Invitation
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default InviteClientModal;
